import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, MapPin, Utensils, Coffee, ThumbsUp, Zap } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_10d419fc-5e2e-447b-b30b-f3a6e5d925a0/artifacts/qt2o5wzu_file_0000000029f471f48789e22e477ba51d.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1608847569619-b5602f65ffa0?w=1200&auto=format&fit=crop&q=80";
const SANDWICH_IMAGE = "https://images.unsplash.com/photo-1667925132898-d3fd2b77c172?w=800&auto=format&fit=crop&q=80";

const features = [
  {
    icon: Utensils,
    title: "Proper Bacon",
    description: "Quality rashers, cooked crisp. None of that limp stuff."
  },
  {
    icon: Coffee,
    title: "Proper Brew",
    description: "Strong, hot tea. Builder's strength, always fresh."
  },
  {
    icon: Zap,
    title: "Fast Service",
    description: "In, out, sorted. We know you've got places to be."
  },
  {
    icon: ThumbsUp,
    title: "Honest Value",
    description: "Fair prices for proper food. No fancy mark-ups."
  }
];

const HomePage = () => {
  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative min-h-[600px] lg:min-h-[700px] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Content */}
        <div className="relative container mx-auto px-4 py-20 lg:py-28 flex items-center min-h-[600px] lg:min-h-[700px]">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-wheat/90 text-wheat-foreground px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
              <Clock className="h-4 w-4" />
              Open Daily 8am – 12pm
            </div>

            {/* Logo for mobile */}
            <div className="mb-6 lg:hidden">
              <img 
                src={LOGO_URL} 
                alt="Bacon & Brews" 
                className="h-24 w-auto drop-shadow-lg"
              />
            </div>

            {/* Heading */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Start Your Day<br />
              <span className="text-wheat">The Proper Way</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg lg:text-xl text-primary-foreground/90 mb-8 max-w-lg animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Fresh bacon sandwiches and strong brewed tea. 
              Built for builders, commuters, and anyone who needs a proper start.
            </p>

            {/* Slogan */}
            <p className="font-display text-2xl italic text-wheat mb-8 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
              "Proper Bacon. Proper Brew."
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Button asChild variant="wheat" size="xl">
                <Link to="/order">Order Now</Link>
              </Button>
              <Button asChild variant="brandOutline" size="xl" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/menu">View Menu</Link>
              </Button>
            </div>

            {/* Location */}
            <div className="flex items-center gap-2 mt-8 text-primary-foreground/80 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <MapPin className="h-5 w-5 text-wheat" />
              <span>59 Torquay Road, Paignton, Devon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-gradient-warm pattern-overlay">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-4">
              What We're About
            </h2>
            <div className="decorative-line mx-auto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={feature.title} 
                className="bg-card border-border shadow-card hover-lift animate-fade-in-up"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-7 w-7 text-accent" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-16 lg:py-24 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative animate-fade-in">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-elevated">
                <img 
                  src={SANDWICH_IMAGE}
                  alt="Delicious bacon sandwich" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 lg:-right-8 bg-accent text-accent-foreground px-6 py-3 rounded-lg shadow-lg">
                <p className="font-display text-lg font-bold">Since 2024</p>
              </div>
            </div>

            {/* Content */}
            <div className="animate-slide-in-right">
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-6">
                No Fuss. Just Good Food.
              </h2>
              <div className="decorative-line mb-6" />
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Bacon & Brews is a proper British breakfast spot. We do bacon sandwiches 
                  and tea – that's it. We do them well, we do them fast, and we don't 
                  charge the earth for them.
                </p>
                <p>
                  Whether you're a builder needing fuel for the morning, a commuter 
                  grabbing something on the way, or just someone who knows what they want – 
                  we've got you sorted.
                </p>
                <p className="font-medium text-foreground">
                  No artisan nonsense. No complicated menus. Just proper food, properly made.
                </p>
              </div>
              <div className="mt-8">
                <Button asChild variant="brand" size="lg">
                  <Link to="/about">Learn More About Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Menu Preview */}
      <section className="py-16 lg:py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-4">
              On The Menu
            </h2>
            <div className="decorative-line mx-auto mb-4" />
            <p className="text-muted-foreground max-w-xl mx-auto">
              Simple choices. Everything made fresh, right in front of you.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="bg-background border-border shadow-card">
              <CardContent className="p-6 lg:p-8">
                {/* Meal Deal Highlight */}
                <div className="bg-wheat/10 border border-wheat/30 rounded-lg p-4 mb-6 text-center">
                  <p className="font-display text-xl font-bold text-accent">Meal Deal — £4.95</p>
                  <p className="text-muted-foreground text-sm">Sandwich + Tea or Coffee + Hashbrown</p>
                </div>

                <div className="space-y-1">
                  <div className="menu-item">
                    <span className="font-medium text-foreground">Bacon Sandwich</span>
                    <span className="font-display font-bold text-accent">£3.50</span>
                  </div>
                  <div className="menu-item">
                    <span className="font-medium text-foreground">Hashbrown</span>
                    <span className="font-display font-bold text-accent">£1.50</span>
                  </div>
                  <div className="menu-item">
                    <span className="font-medium text-foreground">Cuppa Tea</span>
                    <span className="font-display font-bold text-accent">£1.50</span>
                  </div>
                  <div className="menu-item">
                    <span className="font-medium text-foreground">Cuppa Coffee</span>
                    <span className="font-display font-bold text-accent">£1.95</span>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <Button asChild variant="brand" size="lg">
                    <Link to="/menu">See Full Menu</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-hero">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
            Ready for a Proper Breakfast?
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-xl mx-auto">
            Come find us on Torquay Road. We're open every morning, 8am to noon.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="wheat" size="xl">
              <Link to="/order">Order Now</Link>
            </Button>
            <Button asChild variant="brandOutline" size="xl" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/contact">Get Directions</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
